<template>
  <!-- Forzar render por encima de la tabla: teleport al body con backdrop -->
  <teleport to="body">
    <div class="debug-modal-backdrop" @click.self="cerrar">
      <div class="modal-content" :style="{ width: modalWidth }" role="dialog" aria-modal="true">
        <div class="modal-header">
          <h3 class="modal-title">
            {{ $t('actualizar_ticket') }} {{ ticket?.id }}
            <span v-if="creadoPor">- de {{ creadoPor }}</span>
            <!-- Badge que indica modo consulta/solo lectura -->
            <span v-if="isReadOnly" class="view-only-badge">{{ $t('solo_consulta') || 'Solo consulta' }}</span>
          </h3>
          <button type="button" class="btn-close" @click.stop="cerrar" aria-label="Cerrar" title="Cerrar">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">   
          <form @submit.prevent="handleSubmit">
            <div class="form-fields-grid">
              <div class="field-row">
                <label for="categoria" class="form-label-grid">{{ $t('categoria') }}</label>
                <select id="categoria" v-model="form.categoria" class="form-control" required :disabled="isReadOnly || isOperador">
                  <option value="" disabled>{{ $t('seleccione_categoria') }}</option>
                  <option v-for="categoria in categorias" :key="categoria.id" :value="categoria.id">
                    {{ categoria.nombre }}
                  </option>
                </select>
              </div>
              <div class="field-row">
                <label for="prioridad" class="form-label-grid">{{ $t('prioridad') }}</label>
                <select id="prioridad" v-model="form.prioridad" class="form-control" required :disabled="isReadOnly || isOperador">
                  <option value="" disabled>{{ $t('seleccione_prioridad') }}</option>
                  <option v-for="prioridad in prioridades" :key="prioridad.id" :value="prioridad.id">
                    {{ $t('nivel_' + prioridad.nombre.toLowerCase()) }}
                  </option>
                </select>
              </div>
              <div class="field-row field-row-full">
                <label for="descripcion" class="form-label-grid">{{ $t('descripcion') }}</label>
                <textarea id="descripcion" v-model="form.descripcion" class="form-control" disabled required rows="6"
                  style="resize: vertical;"></textarea>
              </div>
            </div>

            <!-- Botón de correo: siempre visible (salvo operadores) - mismo tamaño que 'Elegir archivos' -->
            <div class="form-group">
              <div style="display: inline-block;">
                <button v-if="!isOperador" type="button" class="btn btn-elegir-archivo" :disabled="isOperador"
                  style="background-color: #6c757d; color: #fff; border: none; border-radius: 5px; padding: 6px 12px; font-size: 12px; font-weight: bold; cursor: pointer; transition: background-color 0.3s ease; margin-bottom: 10px;"
                  @click="openEmailModal">
                  {{ $t('email') }}
                </button>
              </div>
            </div>

            <!-- Archivos ya subidos -->
            <div v-if="archivosSubidos.length" class="form-group archivos-subidos">
              <label class="form-label">{{ $t('archivos_ya_subidos') }}</label>
              <div class="adjuntos-scroll">
                <ul>
                  <li v-for="(file, idx) in archivosSubidos" :key="file.id" class="fila-archivo-subido">
                    <div class="col-nombre-archivo">
                      <span class="nombre-archivo-truncado" :title="file.filename" :ref="'fileNameSpan' + idx"
                        v-if="expandedFileName !== idx">
                        {{ getTruncated(file.filename) }}
                      </span>
                      <a v-if="isNameLong(file.filename) && expandedFileName !== idx" href="#"
                        @click.prevent="expandedFileName = idx" class="ver-mas-link">
                        {{ $t('ver_mas') }}
                      </a>
                      <template v-else-if="expandedFileName === idx">
                        <span class="nombre-archivo-expandido" style="word-break:break-all; white-space:normal;">
                          {{ file.filename }}
                          <a href="#" @click.prevent="expandedFileName = null" class="ver-mas-link" style="margin-left:5px;">
                            {{ $t('ver_menos') }}
                          </a>
                        </span>
                      </template>
                    </div>
                    <!-- Comentario (55%) -->
                    <div class="col-comentario">
                      <!-- el input de edición de comentario solo para roles con permiso -->
                      <span v-if="subidoCommentIndex === idx && !isOperador">
                        <input v-model="subidoCommentValue" class="input-comentario" type="text"
                          :placeholder="$t('comentario_opcional')" autofocus />
                        <button type="button" @click="confirmSubidoComment(idx, file.id)" class="icon-btn"
                          :title="$t('confirmar_comentario')">
                          <span class="material-icons action-icon">check_circle</span>
                        </button>
                        <button type="button" @click="cancelSubidoCommentEdit" class="icon-btn"
                          :title="$t('descartar_edicion')">
                          <span class="material-icons action-icon">close</span>
                        </button>
                      </span>
                      <span v-else>
                        <span v-if="file.comment">{{ file.comment.length > 40 ? file.comment.slice(0, 40) + '...' :
                          file.comment
                        }}</span>
                        <a v-if="file.comment && file.comment.length > 40 && expandedSubidoComment !== idx" href="#"
                          @click.prevent="expandedSubidoComment = idx" class="ver-mas-link">{{ $t('ver_mas') }}</a>
                        <template v-if="expandedSubidoComment === idx">
                          {{ file.comment }}
                          <a href="#" @click.prevent="expandedSubidoComment = null" class="ver-mas-link">{{
                            $t('ver_menos') }}</a>
                        </template>
                      </span>
                    </div>
                    <!-- Acciones (15%) -->
                    <div class="col-acciones-archivo">
                      <!-- Icono de previsualizar -->
                      <button
                        type="button"
                        class="icon-btn"
                        :title="$t('previsualizar')"
                        @click="previewFile(file)"
                        style="background:none; border:none; cursor:pointer;"
                      >
                        <span class="material-icons action-icon">visibility</span>
                      </button>
                      <!-- Descargar -->
                      <button type="button" @click="descargarArchivo(file.id, file.filename)" class="icon-btn" :title="$t('descargar_archivo')">
                        <span class="material-icons action-icon">download</span>
                      </button>
                      <!-- ocultar editar comentario para operarios y en modo consulta -->
                      <button v-if="!isOperador && !isReadOnly" type="button" @click="toggleSubidoComment(idx)" class="icon-btn"
                        :title="file.comment ? $t('editar_comentario') : $t('anadir_comentario')">
                        <span class="material-icons action-icon">comment</span>
                      </button>
                      <!-- siempre permitir descargar -->
                      <!-- ocultar eliminar para operarios y en modo consulta -->
                      <button v-if="!isOperador && !isReadOnly" type="button" @click="deleteArchivoSubido(file.id, idx)" class="icon-btn"
                        :title="$t('eliminar_archivo')">
                        <span class="material-icons action-icon">delete</span>
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">{{ $t('archivos_adjuntos') }}</label>
              <div style="position: relative; display: inline-block;">
                <button type="button" class="btn btn-elegir-archivo" :disabled="isReadOnly"
                  style="background-color: #6c757d; color: #fff; border: none; border-radius: 5px; padding: 6px 12px; font-size: 12px; font-weight: bold; cursor: pointer; transition: background-color 0.3s ease; margin-bottom: 10px;"
                  @click="!isReadOnly && $refs.inputFile.click()">
                  {{ $t('elegir_archivos') }}
                </button>
                <input ref="inputFile" type="file" multiple @change="addFiles" accept="image/*,application/pdf"
                  :disabled="isReadOnly"
                  style="opacity:0; position:absolute; left:0; top:0; width:100%; height:100%; cursor:pointer;" />
              </div>
            </div>

            <div v-if="selectedFiles.length" class="form-group">
              <label class="form-label">{{ $t('archivos_seleccionados') }}</label>
              <div class="adjuntos-scroll">
                <ul>
                  <li v-for="(file, idx) in selectedFiles" :key="idx"
                    style="display:flex; align-items:center; border-bottom:1px solid #eee; padding:6px 0;">
                    <div class="col-nombre-archivo">
                      <template v-if="renameIndex === idx">
                        <input v-model="renameBase" style="width: 120px;" type="text"
                          :placeholder="getBaseName(file.file.name)" autofocus />
                        .{{ getExtension(file.file.name) }}
                        <button type="button" @click="confirmRename(idx)" class="icon-btn"
                          :title="$t('confirmar_nombre')">
                          <span class="material-icons action-icon">check_circle</span>
                        </button>
                        <button type="button" @click="cancelRenameEdit" class="icon-btn" :title="$t('cancelar_edicion')">
                          <span class="material-icons action-icon">close</span>
                        </button>
                      </template>
                      <template v-else>
                        <span
                          class="nombre-archivo-truncado"
                          :title="file.file.name"
                          :ref="'selectedFileNameSpan' + idx"
                          v-if="expandedSelectedFileName !== idx"
                        >
                          {{ getTruncated(file.file.name) }}
                        </span>
                        <a
                          v-if="isNameLong(file.file.name) && expandedSelectedFileName !== idx"
                          href="#"
                          @click.prevent="expandedSelectedFileName = idx"
                          class="ver-mas-link"
                        >
                          {{ $t('ver_mas') }}
                        </a>
                        <template v-else-if="expandedSelectedFileName === idx">
                          <span class="nombre-archivo-expandido" style="word-break:break-all; white-space:normal;">
                            {{ file.file.name }}
                            <a href="#" @click.prevent="expandedSelectedFileName = null" class="ver-mas-link" style="margin-left:5px;">
                              {{ $t('ver_menos') }}
                            </a>
                          </span>
                        </template>
                      </template>
                    </div>
                    <div style="flex:2 1 0; min-width:160px; max-width:100%;">
                      <span v-if="commentIndex === idx">
                        <input v-model="commentValue" style="width: 90%;" type="text"
                          :placeholder="$t('comentario_opcional')" autofocus />
                        <button type="button" @click="confirmComment(idx)"
                          style="background:none; border:none; cursor:pointer; margin-left:3px;"
                          :title="$t('confirmar_comentario')">
                          <span class="material-icons action-icon">check_circle</span>
                        </button>
                        <button type="button" @click="cancelCommentEdit"
                          style="background:none; border:none; cursor:pointer; margin-left:3px;"
                          :title="$t('descartar_edicion')">
                          <span class="material-icons action-icon">close</span>
                        </button>
                      </span>
                      <span v-else>
                        <span v-if="file.comentario">{{ file.comentario.length > 40 ? file.comentario.slice(0, 40) + '...' :
                          file.comentario }}</span>
                        <a v-if="file.comentario && file.comentario.length > 40 && expandedComment !== idx" href="#"
                          @click.prevent="expandedComment = idx" style="color:#99172A; margin-left:5px;">{{ $t('ver_mas')
                          }}</a>
                        <template v-if="expandedComment === idx">
                          {{ file.comentario }}
                          <a href="#" @click.prevent="expandedComment = null" style="color:#99172A; margin-left:5px;">{{
                            $t('ver_menos') }}</a>
                        </template>
                      </span>
                    </div>
                    <div style="display:flex; gap:2px; min-width:120px; justify-content:flex-end;">
                      <!-- Icono de previsualizar -->
                      <button
                        type="button"
                        class="icon-btn"
                        :title="$t('previsualizar')"
                        @click="previewFile(file)"
                        style="background:none; border:none; cursor:pointer;"
                      >
                        <span class="material-icons action-icon">visibility</span>
                      </button>
                      <!-- Descargar local -->
                      <button type="button" @click="downloadFile(file)"
                        style="background:none; border:none; cursor:pointer;" :title="$t('descargar_archivo')">
                        <span class="material-icons action-icon">download</span>
                      </button>
                      <button type="button" @click="startRename(idx)"
                        style="background:none; border:none; cursor:pointer;" :title="$t('renombrar')">
                        <span class="material-icons action-icon">edit_square</span>
                      </button>
                      <button type="button" @click="toggleComment(idx)"
                        style="background:none; border:none; cursor:pointer;"
                        :title="file.comentario ? $t('editar_comentario') : $t('anadir_comentario')">
                        <span class="material-icons action-icon">comment</span>
                      </button>
                      <button type="button" @click="downloadFile(file)"
                        style="background:none; border:none; cursor:pointer;" :title="$t('descargar_archivo')">
                        <span class="material-icons action-icon">download</span>
                      </button>
                      <button type="button" @click="removeFile(idx)" style="background:none; border:none; cursor:pointer;"
                        :title="$t('eliminar')">
                        <span class="material-icons action-icon">delete</span>
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <!-- ocultar únicamente el bloque de detalles para operarios -->
            <div class="form-group" v-if="!isOperador">
               <div class="details-header-centered">
                 <label class="form-label">{{ $t('detalles') }}</label>
               </div>
               <div class="details-add-left">
                 <button type="button" class="btn btn-add-detalle" :class="{ disabled: isAddDisabled }"
                   :title="$t('anadir_detalle')" @click="!isAddDisabled && addDetalle()" :disabled="isAddDisabled">
                   {{ $t('anadir_detalle') }}
                 </button>
               </div>
               <div class="table-container" ref="detallesTable">
                 <table class="table">
                   <thead>
                     <tr>
                       <th style="width: 20%;">{{ $t('fecha') }}</th>
                       <th style="width: 50%;">{{ $t('descripcion') }}</th>
                       <th style="width: 30%;">{{ $t('usuario') }}</th>
                       <th style="width: 10%;">{{ $t('acciones') }}</th>
                     </tr>
                   </thead>
                   <tbody>
                     <tr
                       v-for="(detalle, index) in detalles"
                       :key="detalle.id"
                       @dblclick="editDetalle(index)"
                       @keydown.enter="editDetalle(index)"
                       tabindex="0"
                       :class="{ 'row-editable': canEditDetalle(detalle) }"
                     >
                       <td>
                         <span>{{ formatFecha(detalle.fecha) }}</span>
                       </td>
                       <td>
                         <span v-if="editingIndex !== index">
                           <template v-if="!detalle.showFullDescription">
                             {{ truncateText(detalle.descripcion, 60) }}
                             <a v-if="detalle.descripcion && detalle.descripcion.length > 55" href="#"
                               @click.prevent="toggleDetalleDescription(detalle)">{{ $t('ver_mas') }}</a>
                           </template>
                           <template v-else>
                             {{ detalle.descripcion }}
                             <a href="#" @click.prevent="toggleDetalleDescription(detalle)">{{ $t('ver_menos') }}</a>
                           </template>
                         </span>
                         <span v-else>
                           <textarea
                             v-model="detalle.descripcion"
                             :maxlength="maxDetalleDescripcion"
                             @input="onDetalleInput(index)"
                             class="form-control"
                             rows="2"
                             style="resize: vertical; width: 100%;"
                             autofocus
                           ></textarea>
                          <div class="text-muted small" style="text-align: right; margin-top:6px;">
                            {{ remainingChars(detalle) }} {{ $t('caracteres_restantes') || 'caracteres restantes' }}
                          </div>
                           <!-- botones inline eliminados: usar los iconos de la columna de acciones -->
                         </span>
                       </td>
                       <td>
                         <span>{{ detalle.usuario }}</span>
                       </td>
                       <td class="col-acciones">
                         <div class="action-container">
                           <!-- Si la fila está en edición mostramos guardar/cancelar (y permitir eliminar si es nueva) -->
                           <template v-if="editingIndex === index">
                             <button type="button" class="icon-btn" @click="saveDetalle(index)" :title="$t('guardar')">
                               <span class="material-icons action-icon action-square">check_circle</span>
                             </button>
                             <button type="button" class="icon-btn" @click="editingIndex = null" :title="$t('cancelar')">
                               <span class="material-icons action-icon action-square">close</span>
                             </button>
                             <!-- permitir eliminar directamente si es detalle nuevo (id === null) -->
                             <button v-if="detalles[index] && detalles[index].id === null && !isReadOnly" type="button" class="icon-btn"
                               @click="deleteDetalle(index)" :title="$t('eliminar')">
                               <span class="material-icons action-icon action-square delete-icon">delete</span>
                             </button>
                           </template>

                           <!-- Si no está en edición mostramos editar / eliminar según permisos -->
                           <template v-else>
                             <button v-if="canEditDetalle(detalle)" type="button" class="icon-btn" @click="editDetalle(index)"
                               :title="$t('editar')">
                               <span class="material-icons action-icon action-square">edit_square</span>
                             </button>

                             <!-- eliminar solo para detalles persistidos si hay permiso, o para nuevos siempre (no en modo consulta) -->
                             <button v-if="(!detalle || detalle.id === null || (!isOperador && detalle.id)) && !isReadOnly" type="button" class="icon-btn"
                               @click="deleteDetalle(index)" :title="$t('eliminar')">
                               <span class="material-icons action-icon action-square">delete</span>
                             </button>
                           </template>
                         </div>
                       </td>
                     </tr>
                   </tbody>
                 </table>
               </div>
             </div>

            <div class="form-actions" style="display: flex; align-items: center; gap: 12px;">
              <button type="submit" class="btn btn-primary" :class="{ disabled: isReadOnly }" :disabled="isReadOnly">
                {{ $t('actualizar') }}
              </button>
              <button type="button" class="btn-cancel" @click.stop="cerrar">
                {{ $t('cancelar') }}
              </button>
              <span class="material-icons" :title="$t('motivos_cierre_apertura')"
                style="color: #99172A; cursor: pointer; font-size: 2rem; opacity: 1; margin-left: 10px;"
                @click="showMotivosModal = true">
                history
              </span>
            </div>
          </form>
          <TicketMotivosModal v-if="showMotivosModal" :ticketId="ticket.id" @close="showMotivosModal = false" />
        </div>
      </div>
    </div>
  </teleport>

  <!-- EmailModal gestiona su propio teleport -->
  <teleport to="body">
    <div
      v-if="showModal"
      class="email-modal-root"
      @click.self="closeEmailModal"
      role="dialog"
      aria-modal="true"
    >
      <EmailModal
        ref="emailModal"
        :visible="showModal"
        :ticket="ticket"
        :viewOnly="ticket && ticket.estado_id === 3"
        @ticket-created="handleTicketCreated"
        @close="closeEmailModal"
      />
    </div>
  </teleport>

  <!-- PreviewModal único y centralizado -->
  <PreviewModal
    :visible="previewVisible"
    :file="previewFileObj"
    :previewUrl="previewObjectUrl"
    @close="closePreview"
    @download="handlePreviewDownload"
  />

  <teleport to="body" v-if="showOpenCloseModal">
    <div class="no-backdrop-viewport" role="dialog" aria-modal="true">
      <OpenCloseTicketModalView
        :ticketId="selectedTicket?.id"
        :isClosed="selectedTicket?.estado === 'Cerrado'"
        @close="closeOpenCloseModal"
        @submit="handleTicketStateUpdated"
      />
    </div>
  </teleport>
</template>

<script>
import Swal from 'sweetalert2';
import { showAlert } from '@/utils/alerts';
import { formatDateTime } from '@/utils/formatDate';
import api from '../api';
import TicketMotivosModal from '../components/TicketMotivosModal.vue';
import EmailModal from '@/components/EmailModal.vue';
import PreviewModal from '@/components/PreviewModal.vue';

export default {
  name: 'TicketFormModalViewUpdate',
  components: {
    PreviewModal,
    EmailModal,
    TicketMotivosModal
  },
  props: {
    modalWidth: { type: String, default: '700px' },
    ticket: { type: Object, default: null },
    creadoPor: { type: String, default: '' }
  },
  emits: ['close','submit'],
  watch: {
    ticket: {
      immediate: true,
      handler(newTicket) {
        if (newTicket) {
          this.form.categoria = newTicket.categoria_id || newTicket.categoria || '';
          this.form.prioridad = newTicket.prioridad_id || newTicket.prioridad || '';
          this.form.descripcion = newTicket.descripcion || '';
          // si el email modal está abierto, pedirle que recargue la lista de emails
          try {
            if (this.showModal && this.$refs.emailModal && this.$refs.emailModal.loadSentEmails) {
              this.$refs.emailModal.loadSentEmails(5);
            }
          } catch (e) { /* ignore */ }
        }
      },
    },
  },
  data() {
    return {
      categorias: [],
      prioridades: [],
      selectedTicket: null,
      form: {
        categoria: '',
        prioridad: '',
        descripcion: '',
      },
      detalles: [],
      editingIndex: null,
      showFullTicketDescription: false,
      showMotivosModal: false,
      // categorias se cargan desde la API y mostramos el nombre tal cual viene de la base de datos
      selectedFiles: [],
      // preview state simplificado
      previewVisible: false,
      previewFileObj: null,
      previewObjectUrl: '',
      renameIndex: null,
      renameBase: '',
      commentIndex: null,
      commentValue: '',
      expandedComment: null,
      archivosSubidos: [],
      archivosParaBorrar: [],
      subidoCommentIndex: null,
      subidoCommentValue: '',
      expandedSubidoComment: null,
      showModal: false,
      expandedFileName: null,
      isTruncado: {},
      isSelectedTruncado: {},
      expandedSelectedFileName: null,
      filenameTruncLength: 24,
      maxDetalleDescripcion: 1500,
    };
  },
  computed: {
    isOperador() {
      const r = sessionStorage.getItem('user_role');
      const parsed = parseInt(r, 10);
      return r === 'operador' || (!Number.isNaN(parsed) && parsed === 4);
    },
    isAddDisabled() {
      return this.isReadOnly || (this.ticket && this.ticket.estado_id === 3) || this.isOperador;
    },
    isReadOnly() {
      // Permitir forzar modo solo lectura desde el padre (forceReadOnly) o por estado (estado_id === 3)
      return !!(this.forceReadOnly || (this.ticket && this.ticket.estado_id === 3));
    }
  },
  mounted() {
    this.loadCategorias();
    this.loadPrioridades();
    this.loadDetalles();
    this.loadArchivosSubidos();
    this.$nextTick(() => {
      this.archivosSubidos.forEach((_, idx) => this.checkTruncado(idx));
      this.selectedFiles.forEach((_, idx) => this.checkSelectedTruncado(idx));
    });
    window.addEventListener('resize', this.onResize);
    this._escHandler = (e) => { if (e.key === 'Escape') this.$emit('close'); };
    window.addEventListener('keydown', this._escHandler);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this._escHandler);
    try { document.body.classList.remove('email-open'); } catch (e) { /* ignore */ }
  },
  methods: {
    cerrar() {
      // cerrar modal principal y limpiar posible estado de email abierto
      this.showModal = false;
      try { document.body.classList.remove('email-open'); } catch (e) { /* ignore */ }
      this.$emit('close');
    },
    onResize() {
      this.archivosSubidos.forEach((_, idx) => this.checkTruncado(idx));
      this.selectedFiles.forEach((_, idx) => this.checkSelectedTruncado(idx));
    },
    addFiles(event) {
      const nuevos = Array.from(event.target.files);
      const archivosValidos = nuevos.filter(file => file.type === 'application/pdf' || file.type.startsWith('image/'));
      if (archivosValidos.length < nuevos.length) {
        this.showToast('Solo se permiten imágenes y archivos PDF.', "error");
      }
      const nuevosArchivos = archivosValidos.map(file => ({ file, comentario: '' }));
      this.selectedFiles = [...this.selectedFiles, ...nuevosArchivos];
      event.target.value = "";
      this.$nextTick(() => {
        this.selectedFiles.forEach((_, idx) => this.checkSelectedTruncado(idx));
      });
    },
    removeFile(idx) {
      this.selectedFiles.splice(idx, 1);
    },
    closeModal() {
      this.showModal = false;
    },
    handleTicketCreated(payload) {
      // cerrar email y limpiar estado
      this.showModal = false;
      try { document.body.classList.remove('email-open'); } catch (e) { /* ignore */ }
      this.showToast('Correo enviado correctamente', 'success');
      // si el modal padre aún está montado y tiene ref al EmailModal, recargar su lista
      try {
        if (this.$refs.emailModal && this.$refs.emailModal.loadSentEmails) {
          this.$refs.emailModal.loadSentEmails(5);
        }
      } catch (e) { /* ignore */ }
    },
    // helper si se necesita cerrar desde otro sitio
    closeModal() {
      this.closeEmailModal();
    },
    startRename(idx) {
      this.renameIndex = idx;
      this.renameBase = this.getBaseName(this.selectedFiles[idx].file.name);
    },
    confirmRename(idx) {
      if (this.renameBase.trim() === '') {
        this.showToast('El nombre es obligatorio', "error");
        return;
      }
      const oldObj = this.selectedFiles[idx];
      const ext = this.getExtension(oldObj.file.name);
      const newName = ext ? `${this.renameBase}.${ext}` : this.renameBase;
      const renamedFile = new File([oldObj.file], newName, { type: oldObj.file.type });
      this.selectedFiles[idx] = {
        file: renamedFile,
        comentario: oldObj.comentario || ''
      };
      this.renameIndex = null;
      this.renameBase = '';
    },
    cancelRenameEdit() {
      this.renameIndex = null;
      this.renameBase = '';
    },
    getBaseName(filename) {
      const lastDot = filename.lastIndexOf('.');
      return lastDot === -1 ? filename : filename.substring(0, lastDot);
    },
    getExtension(filename) {
      const lastDot = filename.lastIndexOf('.');
      return lastDot === -1 ? '' : filename.substring(lastDot + 1);
    },
    toggleComment(idx) {
      if (this.commentIndex === idx) {
        this.commentIndex = null;
        this.commentValue = '';
      } else {
        this.commentIndex = idx;
        this.commentValue = this.selectedFiles[idx].comentario || '';
      }
    },
    confirmComment(idx) {
      this.selectedFiles[idx].comentario = this.commentValue.trim() ? this.commentValue : '';
      this.commentIndex = null;
      this.commentValue = '';
    },
    cancelCommentEdit() {
      this.commentIndex = null;
      this.commentValue = '';
    },
    downloadFile(file) {
      const url = URL.createObjectURL(file.file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    async descargarArchivo(fileId, fileName) {
      const response = await api.get(`/tickets/files/download/${fileId}`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
        },
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    async handleSubmit() {
      try {
        const cabeceraResp = await api.put(`/tickets/${this.ticket.id}/update-by-user`, {
          descripcion: this.form.descripcion,
          categoria_id: this.form.categoria,
          prioridad_id: this.form.prioridad,
        }, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
          },
        });

        const nuevosDetalles = this.detalles.filter(detalle => detalle.id === null);
        for (const detalle of nuevosDetalles) {
          // Solo enviar los campos requeridos para crear un detalle
                await api.post('/tickets/detalles', {
            ticket_id: this.ticket.id,
            descripcion: detalle.descripcion,
            fecha: detalle.fecha, // opcional, si existe
                }, { headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` } });
        }

        const detallesExistentes = this.detalles.filter(detalle => detalle.id !== null);
        for (const detalle of detallesExistentes) {
          // Solo enviar los campos permitidos para actualizar un detalle
                await api.put(`/tickets/detalles/${detalle.id}`, {
            descripcion: detalle.descripcion,
            fecha: detalle.fecha, // opcional, si existe
          }, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` },
          });
        }

        for (const file of this.archivosParaBorrar) {
          await api.delete(`/tickets/files/${file.id}`, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
            },
          });
        }
        this.archivosParaBorrar = [];

        for (const obj of this.selectedFiles) {
          const formData = new FormData();
          formData.append('file', obj.file);
          formData.append('comentario', obj.comentario || '');
          await api.post(`/tickets/${this.ticket.id}/upload-file`, formData, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
              'Content-Type': 'multipart/form-data',
            },
          });
        }

        const ticketResp = await api.get(`/tickets/${this.ticket.id}`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
          },
        });

        this.form.prioridad = ticketResp.data.ticket.prioridad_id || ticketResp.data.ticket.prioridad || '';
        this.form.categoria = ticketResp.data.ticket.categoria_id || ticketResp.data.ticket.categoria || '';
        this.form.descripcion = ticketResp.data.ticket.descripcion || '';

        await this.loadDetalles();
        await this.loadArchivosSubidos();

        // si el EmailModal está abierto, recargar la lista de emails enviados
        try {
          if (this.showModal && this.$refs.emailModal && this.$refs.emailModal.loadSentEmails) {
            await this.$refs.emailModal.loadSentEmails(5);
          }
        } catch (e) { /* ignore */ }

        await showAlert('success', this.$t('ticket_actualizado') || 'Éxito', this.$t('ticket_actualizado_text', { id: this.ticket?.id }) || 'Ticket actualizado correctamente');
      } catch (error) {
        await showAlert('error', this.$t('sweetalert_error_title') || 'Error', this.$t('error_actualizar_ticket') || 'Error al actualizar el ticket');
      } finally {
        this.$emit('submit');
      }
    },
    async loadCategorias() {
      try {
        const response = await api.get('/tickets/categorias', {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
            },
          });
        // Normalizar la estructura igual que en WelcomeView
        this.categorias = (response.data.categorias || []).map((categoria) => ({
          id: categoria.id,
          nombre: categoria.nombre,
        }));
      } catch (error) {
        console.error('Error al cargar las categorías:', error.response?.data || error.message);
      }
    },
    async loadPrioridades() {
      try {
        const response = await api.get('/tickets/prioridades', {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
          },
        });
        this.prioridades = response.data.prioridades;
      } catch (error) {
        console.error('Error al cargar las prioridades:', error.message);
      }
    },
    async loadDetalles() {
      try {
        const response = await api.get(`/tickets/${this.ticket.id}/detalles`, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
            },
          });

        this.detalles = response.data.detalles
          .map((detalle) => ({
            id: detalle.id,
            fecha: detalle.fecha,
            descripcion: detalle.descripcion,
            usuario: detalle.usuario?.nombre_completo || 'Desconocido',
            showFullDescription: false,
          }))
          .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

      } catch (error) {
        console.error('Error al cargar los detalles:', error.message);
      }
    },
    async loadArchivosSubidos() {
      try {
        const response = await api.get(`/tickets/${this.ticket.id}/files`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
          },
        });
        this.archivosSubidos = response.data.files || [];
        this.$nextTick(() => {
          this.archivosSubidos.forEach((_, idx) => this.checkTruncado(idx));
        });
      } catch (error) {
        console.error('Error al cargar archivos subidos:', error.message);
      }
    },
    addDetalle() {
      if (this.isOperador) return;
      if (this.ticket.estado_id === 3) {
        console.warn('No se pueden añadir detalles a un ticket cerrado.');
        return;
      }

      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

  const usuarioLogueado = sessionStorage.getItem('user_name');

      this.detalles.unshift({
        id: null,
        fecha: timestamp,
        descripcion: '',
        usuario: usuarioLogueado || 'Desconocido',
        usuario_id: this.ticket.usuario_id,
        showFullDescription: false,
      });

      this.editingIndex = 0;
    },

    canEditDetalle(detalle) {
      if (!detalle) return false;
      // detalles añadidos en la sesión siempre editables (id === null)
      if (detalle.id === null) return true;
      // detalles persistidos solo si no es operador y ticket no está cerrado
      if (this.isOperador) return false;
      if (this.ticket && this.ticket.estado_id === 3) return false;
      return true;
    },

    editDetalle(index) {
      const detalle = this.detalles[index];
      if (!detalle) return;
      // no intentar editar si no está permitido
      if (!this.canEditDetalle(detalle)) return;

      // Si el detalle ya existe en servidor, respetar restricciones habituales
      if (detalle.id !== null) {
        if (this.isOperador) return;
        if (this.ticket && this.ticket.estado_id === 3) return;
      }

      this.editingIndex = index;
    },

    // permitir guardar cambios locales en detalles creados en la sesión
    saveDetalle(index) {
      const detalle = this.detalles[index];
      if (!detalle) return;

      // Si el detalle ya existe en servidor, respetar restricciones
      if (detalle.id !== null) {
        if (this.isOperador) return;
        // aquí podrías enviar update al servidor si quieres; actualmente la UI simplemente cierra edición
      }

      // persistir localmente (si es id === null se subirá en handleSubmit)
      this.editingIndex = null;
    },

    // permitir eliminar detalles no persistidos incluso si el ticket está cerrado / operador
    async deleteDetalle(index) {
      const detalle = this.detalles[index];
      if (!detalle) return;
      // No permitir eliminar si estamos en modo consulta
      if (this.isReadOnly) {
        console.warn('Intento de eliminar detalle en modo consulta - acción bloqueada');
        this.showToast(this.$t?.('accion_no_permitida') || 'Acción no permitida en modo consulta', 'error');
        return;
      }

      // Si es detalle persistido, seguir usando la API y respetar permisos
      if (detalle.id) {
        if (this.isOperador) return;
        try {
          await api.delete(`/tickets/detalles/${detalle.id}`, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
            },
          });
        } catch (error) {
          console.error('Error al eliminar el detalle:', error.message);
          return;
        }
      }

      // eliminar localmente (para nuevos detalles id === null o tras borrado en servidor)
      this.detalles.splice(index, 1);
    },

    formatFecha(fecha) {
      // include seconds for detalle timestamps
      return formatDateTime(fecha, true);
    },
    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    toggleDetalleDescription(detalle) {
      detalle.showFullDescription = !detalle.showFullDescription;
    },
    getDownloadUrl(fileId) {
      return `/api/tickets/files/download/${fileId}`;
    },
    toggleSubidoComment(idx) {
      // No permitir editar comentarios si el ticket está en modo consulta o si es operador
      if (this.isReadOnly || this.isOperador) return;
      if (this.subidoCommentIndex === idx) {
        this.subidoCommentIndex = null;
        this.subidoCommentValue = '';
      } else {
        this.subidoCommentIndex = idx;
        this.subidoCommentValue = this.archivosSubidos[idx].comment || '';
      }
    },
    async confirmSubidoComment(idx, fileId) {
      try {
        await api.put(`/tickets/files/${fileId}/comentario`, {
          comment: this.subidoCommentValue,
        });
        this.archivosSubidos[idx].comment = this.subidoCommentValue;
      } catch (error) {
        this.showToast('Error al actualizar el comentario', 'error');
      }
      this.subidoCommentIndex = null;
      this.subidoCommentValue = '';
    },
    cancelSubidoCommentEdit() {
      this.subidoCommentIndex = null;
      this.subidoCommentValue = '';
    },
    onDetalleInput(index) {
      const detalle = this.detalles[index];
      if (!detalle || !detalle.descripcion) return;
      if (detalle.descripcion.length > this.maxDetalleDescripcion) {
        detalle.descripcion = detalle.descripcion.slice(0, this.maxDetalleDescripcion);
      }
    },
    remainingChars(detalle) {
      try {
        const len = detalle && typeof detalle.descripcion === 'string' ? detalle.descripcion.length : 0;
        const remaining = this.maxDetalleDescripcion - len;
        return remaining >= 0 ? remaining : 0;
      } catch (e) {
        return this.maxDetalleDescripcion;
      }
    },
    async deleteArchivoSubido(fileId, idx) {
      const result = await Swal.fire({
        title: this.$t('eliminar_archivo_confirmacion'),
        text: this.$t('eliminar_archivo_texto'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#99172A',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('si_eliminar'),
        cancelButtonText: this.$t('swal_eliminar_ticket_cancel')
      });

      if (result.isConfirmed) {
        this.archivosParaBorrar.push(this.archivosSubidos[idx]);
        this.archivosSubidos.splice(idx, 1);
        this.showToast(this.$t('archivo_marcado_para_eliminar'), 'success');
      }
    },
    restaurarArchivo(idx) {
      const file = this.archivosParaBorrar[idx];
      this.archivosParaBorrar.splice(idx, 1);
      this.archivosSubidos.push(file);
    },
    getTruncated(name) {
      if (!name) return '';
      const ext = this.getExtension(name);
      const base = this.getBaseName(name);
      const max = this.filenameTruncLength;
      if (name.length <= max) return name;
      if (!ext) {
        return base.slice(0, Math.max(3, max - 3)) + '...';
      }
      const extWithDot = '.' + ext;
      const reserve = extWithDot.length + 3;
      const allowedBase = Math.max(3, max - reserve);
      const truncatedBase = base.length > allowedBase ? base.slice(0, allowedBase) + '...' : base;
      return truncatedBase + extWithDot;
    },
    isNameLong(name) {
      if (!name) return false;
      return name.length > this.filenameTruncLength;
    },
    checkTruncado(idx) {
      this.$nextTick(() => {
        let el = this.$refs['fileNameSpan' + idx];
        if (Array.isArray(el)) el = el[0];
        const truncated = !!(el && el.scrollWidth > el.clientWidth);
        if (!this.isTruncado || typeof this.isTruncado !== 'object') this.isTruncado = {};
        this.isTruncado[idx] = truncated;
      });
    },
    checkSelectedTruncado(idx) {
      this.$nextTick(() => {
        let el = this.$refs['selectedFileNameSpan' + idx];
        if (Array.isArray(el)) el = el[0];
        const truncated = !!(el && el.scrollWidth > el.clientWidth);
        if (!this.isSelectedTruncado || typeof this.isSelectedTruncado !== 'object') this.isSelectedTruncado = {};
        this.isSelectedTruncado[idx] = truncated;
      });
    },
    openEmailModal() {
      if (this.isOperador) {
        this.showToast(this.$t?.('sin_permiso_enviar_email') || 'No tiene permiso para enviar correos', 'error');
        return;
      }
      // si no hay ticket aún, bloquear
      if (!this.ticket || !this.ticket.id) {
        this.showToast(this.$t?.('ticket_no_seleccionado') || 'Ticket no seleccionado', 'error');
        return;
      }
      // marcar que el email está abierto para que el backdrop padre deje de bloquear
      try { document.body.classList.add('email-open'); } catch (e) { /* ignore */ }
      this.showModal = true;
      // si está en modo solo lectura, informar al usuario (opcional)
      if (this.isReadOnly) {
        this.showToast(this.$t?.('modo_consulta_emails') || 'Modo consulta: solo se muestran los emails enviados', 'info');
      }
    },
    // cerrar el EmailModal (teleport) y limpiar clase en body
    closeEmailModal() {
      this.showModal = false;
      try { document.body.classList.remove('email-open'); } catch (e) { /* ignore */ }
    },
    // abrir preview: aceptar objeto local (selectedFiles entry) o archivo remoto (obj con id/filename)
    async previewFile(file) {
      console.log('[previewFile] called', file);
      if (!file) return;

      // limpiar preview anterior
      if (this.previewObjectUrl) {
        try { URL.revokeObjectURL(this.previewObjectUrl); } catch (e) { /* ignore */ }
        this.previewObjectUrl = '';
      }

      // si es archivo local (estructura { file, comentario })
      if (file.file instanceof File) {
        const url = URL.createObjectURL(file.file);
        this.previewFileObj = file.file;
        this.previewObjectUrl = url;
        this.previewVisible = true;
        document.body.classList.add('preview-open');
        return;
      }

      // si es archivo remoto (tiene id) -> descargar blob y crear URL
      try {
        const response = await api.get(`/tickets/files/download/${file.id}`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` }
        });
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        this.previewFileObj = { name: file.filename || file.name || 'archivo', type: blob.type };
        this.previewObjectUrl = url;
        this.previewVisible = true;
        document.body.classList.add('preview-open');
      } catch (err) {
        console.error('Error al preparar preview:', err);
        this.previewVisible = false;
        this.previewFileObj = null;
        this.previewObjectUrl = '';
      }
    },
    closePreview() {
      if (this.previewObjectUrl) {
        try { URL.revokeObjectURL(this.previewObjectUrl); } catch (e) { /* ignore */ }
      }
      this.previewObjectUrl = '';
      this.previewFileObj = null;
      this.previewVisible = false;
      document.body.classList.remove('preview-open');
    },
    handlePreviewDownload() {
      // descarga simple usando la URL preparada
      if (!this.previewObjectUrl) return;
      const a = document.createElement('a');
      a.href = this.previewObjectUrl;
      a.download = (this.previewFileObj && (this.previewFileObj.name || this.previewFileObj.filename)) || 'archivo';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    showToast(message, type = 'info') {
      // Tu implementación de toast aquí
      console.log(`${type}: ${message}`);
    },
    openOpenCloseModal(ticket) {
      console.log('Abriendo modal de cierre/reapetura para ticket:', ticket.id);
      this.selectedTicket = ticket;
      this.showOpenCloseModal = true;
      console.log('showOpenCloseModal establecido a:', this.showOpenCloseModal);
    },
  },
};
</script>

  props: {
    ticket: { type: Object, required: false },
    creadoPor: { type: String, required: false },
    // Si se fuerza modo solo lectura desde el padre
    forceReadOnly: { type: Boolean, default: false },
  },
<style scoped>
.modal-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: fixed;
  top: 5%;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
}
.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 80%;
  max-width: 1200px;
  max-height: 90vh;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px; /* aumentado para más separación entre título y botón */
  background-color: #99172A;
  color: white;
  padding: 15px 20px;
  left: 0px;
  top: 0px;
  box-sizing: border-box;
  position: relative;
}

.modal-title {
  flex: 1 1 0;
  min-width: 0; /* necesario para truncar correctamente */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
  padding-right: 24px; /* más espacio antes del botón */
  font-weight: 400;
  color: #fff;
}

.view-only-badge {
  display: inline-block;
  margin-left: 10px;
  background: rgba(255,255,255,0.12);
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
}

/* Estilo del botón de cierre: fondo más visible, forma circular y aspa claramente blanca */
.btn-close {
  flex: 0 0 auto;
  margin-left: 12px;
  z-index: 3;
  background: rgba(255,255,255,0.12); /* más contraste */
  border-radius: 50%;
  padding: 8px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s ease, transform 0.08s ease;
  outline: none;
}

.btn-close:focus {
  box-shadow: 0 0 0 3px rgba(255,255,255,0.12);
}

.btn-close:hover {
  background: rgba(255,255,255,0.22);
  transform: translateY(-1px);
}

.btn-close .material-icons {
  color: #ffffff; /* aspa más blanca */
  font-size: 22px;
  line-height: 1;
  opacity: 1;
  text-shadow: 0 1px 0 rgba(0,0,0,0.6);
  filter: none;
}

/* en pantallas pequeñas permitir que el título haga wrap en 2 líneas antes de truncar */
@media (max-width: 420px) {
  .modal-title {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: 3.2em;
  }
}

.btn-cancel { background-color: #6c757d; color: #fff; border: none; border-radius: 4px; padding: 10px 20px; font-size: 1rem; font-weight: bold; cursor: pointer; transition: background-color 0.3s ease; height: 44px; line-height: 24px; display: inline-block; vertical-align: middle; }
.btn-cancel:hover { background-color: #495057; }
.modal-body { padding: 20px; overflow-y: auto; max-height: auto; }
.form-fields-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px 32px; margin-bottom: 20px; }
.field-row { display: flex; align-items: center; gap: 12px; }
.field-row-full { grid-column: 1 / span 2; }
.form-label-grid { min-width: 90px; font-weight: bold; text-align: left; }
.form-control { flex: 1 1 0%; margin-bottom: 0; }
textarea.form-control { height: 100px; resize: vertical; }
.form-actions { text-align: right; }
.btn-primary { background-color: #99172A; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; }
.btn-primary.disabled { background-color: #ccc; cursor: not-allowed; }
.btn-primary:hover:not(.disabled) { background-color: #7A1222; }
.table { width: 100%; border-collapse: collapse; margin-top: 20px; }
.table th, .table td { border: 1px solid #ced4da; padding: 10px; text-align: left; }
.table th { background-color: #f8f9fa; font-weight: bold; }
.table td:nth-child(2) { width: 50%; }
.table td:nth-child(1), .table td:nth-child(3) { width: 20%; }
.table td:nth-child(4) { width: 10%; }
.table-container { max-height: 180px; overflow-y: auto; border: 1px solid #ced4da; border-radius: 8px; }
.table thead th { position: sticky; top: 0; background-color: #f8f9fa; z-index: 1; }
.btn-sm { padding: 5px 10px; font-size: 0.875rem; }
.details-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.details-header .form-label { font-weight: bold; font-size: 1.2rem; }
.details-header-centered { display: flex; justify-content: center; align-items: center; margin-bottom: 0; }
.details-header-centered .form-label { font-weight: bold; font-size: 1rem; border: none; padding: 0; background: none; }
.details-add-left { display: flex; justify-content: flex-start; align-items: center; margin-bottom: 10px; }
.details-actions { display: flex; gap: 15px; align-items: center; }
.icon-action { cursor: pointer; color: #99172A; font-size: 1.5rem; display: flex; align-items: center; justify-content: center; }
.icon-action.disabled { color: #ccc; cursor: not-allowed; }
.icon-action.disabled:hover { color: #ccc; }
.material-icons { font-size: 1.5rem; vertical-align: middle; }
.table tbody tr:nth-child(even) { background-color: #f2f2f2; }
.table tbody tr:nth-child(odd) { background-color: #ffffffad; }
.btn-add-detalle { background-color: #2e7d32 !important; color: #fff !important; border: none; border-radius: 5px; padding: 6px 12px; font-size: 12px; font-weight: bold; cursor: pointer; transition: background-color 0.3s ease; margin-bottom: 10px; }
.btn-add-detalle:hover:not(.disabled) { background-color: #1b5e20 !important; color: #fff !important; }
.btn-add-detalle.disabled { background-color: #ccc !important; color: #999 !important; cursor: not-allowed; }
.form-group { margin-bottom: 20px; }
.form-label { display: block; margin-bottom: 5px; font-weight: bold; }
ul { padding-left: 0; list-style: none; }
li { margin-bottom: 8px; display: flex; align-items: center; }
.adjuntos-scroll { max-height: 180px; overflow-y: auto; border: 1px solid #ced4da; border-radius: 4px; background: #fafafa; padding: 4px 0; }
.archivos-subidos { margin-top: 20px; }
.archivos-subidos .form-label { font-weight: bold; margin-bottom: 10px; }
.archivos-subidos ul { padding-left: 0; list-style: none; }
.archivos-subidos li { margin-bottom: 8px; display: flex; align-items: center; }
.archivos-para-borrar { margin-top: 20px; }
.archivos-para-borrar .form-label { font-weight: bold; margin-bottom: 10px; }
.archivos-para-borrar ul { padding-left: 0; list-style: none; }
.archivos-para-borrar li { margin-bottom: 8px; display: flex; align-items: center; }
.nombre-archivo-truncado {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}
.ver-mas-link { color: #236ef8; margin-left: 5px; white-space: nowrap; }
.nombre-archivo-expandido { white-space: normal; display: block; }
.action-container { display: flex; gap: 4px; align-items: center; min-width: 90px; justify-content: flex-end; }
.material-icons.action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #6c757d; /* gris neutro */
  color: #ffffff !important;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  font-size: 18px !important;
  line-height: 1;
  margin: 0 4px;
}

/* color gris para la papelerita de eliminar en acciones de detalle */
.material-icons.action-icon.delete-icon {
  color: #6c757d; /* tono gris */
}
.fila-archivo-subido { display: flex; width: 100%; box-sizing: border-box; align-items: center; border-bottom: 1px solid #eee; padding: 6px 0; }
.col-nombre-archivo { width: 30%; min-width: 0; max-width: 350px; overflow: hidden; padding-left: 3px; display: flex; align-items: center; }
.col-comentario { width: 55%; min-width: 160px; max-width: 100%; box-sizing: border-box; overflow: hidden; display: flex; align-items: center; padding-left: 3px; }
.input-comentario { width: 90%; }
.col-acciones-archivo { width: 15%; min-width: 70px; display: flex; gap: 1px; justify-content: flex-end; box-sizing: border-box; align-items: center; }
.icon-btn { background: none; border: none; cursor: pointer; margin-left: 3px; padding: 0; }
.row-editable { cursor: pointer; }
.row-editable:focus { outline: none; box-shadow: inset 0 0 0 2px rgba(153,23,42,0.08); }

/* Estos estilos NO son scoped porque afectan elementos fuera del componente (teleport) */
:global(.debug-modal-backdrop) {
  position: fixed !important;
  inset: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 12px !important;
  box-sizing: border-box !important;
  background: rgba(0,0,0,0.9) !important;
  z-index: 2147483646 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

/* cuando se abre el preview (body.preview-open) ocultamos el backdrop principal
   para que el PreviewModal (teleport: body) sea visible */
:global(body.preview-open .debug-modal-backdrop) {
  display: none !important;
}

:global(.modal-content) {
  box-sizing: border-box !important;
  width: auto !important;
  max-width: calc(100% - 24px) !important;
  margin: 0 !important;
  position: relative !important;
  z-index: 2147483647 !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

:global(.modal-content > *) {
  max-width: 100% !important;
  box-sizing: border-box !important;
}

@media (max-width: 768px) {
  :global(.debug-modal-backdrop) { padding: 8px !important; }
  :global(.modal-content) {
    width: calc(100% - 16px) !important;
    max-width: calc(100% - 16px) !important;
    max-height: calc(100vh - 24px) !important;
  }
}

/* cuando se abre EmailModal con teleport, evitar que el backdrop principal bloquee interacción */
:global(body.email-open .debug-modal-backdrop) {
  visibility: hidden !important;
  pointer-events: none !important;
}
:global(body.email-open .modal-content) {
  visibility: hidden !important;
  pointer-events: none !important;
}
/* espacio para el modal teletransportado */
.email-modal-root {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: auto; /* sin forzar z-index: el modal teletransportado está encima por el cuerpo y la backdrop está ocultada */
}
</style>